import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  ClipboardCheck,
  Code2,
  MessageSquare,
  Quote,
  Search,
  Sparkles,
  Target,
  User,
  Users,
} from 'lucide-react';
import CustomerInsights from './CustomerInsights';
import CaseStudyChallenge from './CaseStudyChallenge';

const CTA_HREF = '/hirewithus/book-demo';
const CTA_LABEL = 'Experience SearchJob.ai with us';

const flow = [
  { label: 'Resume', icon: ClipboardCheck },
  { label: 'AI interview', icon: MessageSquare },
  { label: 'Coding test', icon: Code2 },
  { label: 'AI follow-up', icon: Sparkles, strong: true },
  { label: 'Live interview', icon: Users },
  { label: 'Shortlist', icon: Target, strong: true },
];

const codingTestPoints = [
  'Code submitted',
  'AI asks “Why?”',
  'Candidate explains',
  'How they think',
];

const codingFollowUpStages = [
  { label: 'Code submitted', detail: 'A technical solution is shared.', icon: Code2 },
  { label: 'AI asks “Why?”', detail: 'The follow-up probes the reasoning.', icon: Sparkles, emphasis: true },
  { label: 'Candidate explains', detail: 'They explain their own approach.', icon: MessageSquare },
  { label: 'Evidence for review', detail: 'The team gets stronger context.', icon: Target },
];

const flowTints = {
  Resume: 'bg-[hsl(152_42%_95%)] text-primary',
  'AI interview': 'bg-[hsl(210_60%_96%)] text-[hsl(210_45%_42%)]',
  'Coding test': 'bg-[hsl(28_80%_95%)] text-[hsl(24_55%_42%)]',
  'Live interview': 'bg-[hsl(262_40%_96%)] text-[hsl(262_35%_45%)]',
  Shortlist: 'bg-[hsl(158_40%_95%)] text-primary',
};

function WhyDepth() {
  return (
    <span className="relative flex h-40 w-full items-center justify-center" aria-hidden="true">
      <span className="w-36 rounded-xl border border-primary/15 bg-white px-3 py-3 shadow-sm">
        <span className="flex gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-primary/25" />
          <span className="h-1.5 w-1.5 rounded-full bg-primary/15" />
          <span className="h-1.5 w-1.5 rounded-full bg-primary/10" />
        </span>
        <span className="mt-2.5 block h-1.5 w-full rounded-full bg-primary/20" />
        <span className="mt-1.5 block h-1.5 w-4/5 rounded-full bg-primary/15" />
        <span className="ml-3 mt-1.5 block h-1.5 w-3/5 rounded-full bg-primary/20" />
        <span className="mt-1.5 block h-1.5 w-1/2 rounded-full bg-primary/15" />
      </span>
      <span className="mn-glass absolute left-1/2 top-7 ml-3 flex h-9 w-9 items-center justify-center rounded-full border border-primary/20 bg-white text-primary shadow-sm">
        <Search className="h-4 w-4" />
      </span>
    </span>
  );
}

function WhyEvidence() {
  const sources = [
    { label: 'Score', icon: ClipboardCheck },
    { label: 'AI interview', icon: MessageSquare },
    { label: 'Coding', icon: Code2 },
  ];
  return (
    <span className="relative block h-44 w-full" aria-hidden="true">
      <span className="absolute inset-x-2 top-0 grid grid-cols-3 gap-2">
        {sources.map((source, index) => {
          const Icon = source.icon;
          return (
            <span key={source.label} style={{ '--i': index }} className="mn-src flex flex-col items-center gap-1 text-[10px] font-semibold text-[hsl(222_24%_28%)]">
              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white text-primary shadow-sm">
                <Icon className="h-4 w-4" />
              </span>
              {source.label}
            </span>
          );
        })}
      </span>
      <svg className="absolute inset-x-6 top-12 h-14 w-[calc(100%-3rem)]" viewBox="0 0 240 56" fill="none" aria-hidden="true">
        <path className="mn-link" style={{ '--i': 0 }} d="M40 0 L120 56" pathLength="1" stroke="hsl(165 37% 22% / 0.35)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        <path className="mn-link" style={{ '--i': 1 }} d="M120 0 L120 56" pathLength="1" stroke="hsl(165 37% 22% / 0.35)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        <path className="mn-link" style={{ '--i': 2 }} d="M200 0 L120 56" pathLength="1" stroke="hsl(165 37% 22% / 0.35)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </svg>
      <span className="absolute bottom-0 left-1/2 flex -translate-x-1/2 flex-col items-center">
        <span className="relative flex h-12 w-12 items-center justify-center rounded-full border border-primary/15 bg-white text-primary shadow-sm">
          <User className="h-6 w-6" />
        </span>
      </span>
    </span>
  );
}

function WhyTime() {
  return (
    <span className="flex h-40 w-full items-center justify-center gap-2 sm:gap-3" aria-hidden="true">
      <span className="grid grid-cols-3 gap-1">
        {Array.from({ length: 6 }, (_, index) => (
          <span key={index} className="mn-drop flex h-6 w-6 items-center justify-center rounded-full border border-border bg-white text-primary/50">
            <User className="h-3 w-3" />
          </span>
        ))}
      </span>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(210_60%_97%)] text-[hsl(210_45%_42%)]">
        <Sparkles className="h-4 w-4" />
      </span>
      <span className="flex gap-1.5">
        {[0, 1].map((index) => (
          <span key={index} className="relative flex h-8 w-8 items-center justify-center rounded-full border border-primary/15 bg-white text-primary shadow-sm">
            <User className="h-4 w-4" />
          </span>
        ))}
      </span>
      <span className="mn-meet flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/15 bg-white text-primary shadow-sm">
        <Users className="h-5 w-5" />
      </span>
    </span>
  );
}
const buttonPrimary =
  'inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

function useSeen(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, seen];
}

function useFlowMotion(ref) {
  const [phase, setPhase] = useState('still');
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const played = { current: false };
    const sync = (visible) => {
      if (media.matches) {
        played.current = false;
        setPhase('still');
        return;
      }
      if (visible) {
        played.current = true;
        setPhase('play');
        return;
      }
      setPhase(played.current ? 'pause' : 'still');
    };
    const observer = new IntersectionObserver(([entry]) => {
      sync(entry.isIntersecting && entry.intersectionRatio >= 0.22);
    }, { threshold: [0, 0.22, 0.55] });
    observer.observe(node);
    const onChange = () => {
      const rect = node.getBoundingClientRect();
      sync(rect.top < window.innerHeight && rect.bottom > 0);
    };
    media.addEventListener('change', onChange);
    return () => {
      observer.disconnect();
      media.removeEventListener('change', onChange);
    };
  }, [ref]);
  return phase;
}

function Card({ title, body, children, icon: Icon, accent = false }) {
  return (
    <article className={`flex h-full flex-col rounded-[20px] border p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 ${accent ? 'border-primary/30 bg-white' : 'border-border bg-[hsl(158_45%_98%)]'}`}>
      {Icon ? (
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}
      <h3 className={`${Icon ? 'mt-3' : ''} text-base font-semibold text-foreground`}>{title}</h3>
      {body ? <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p> : null}
      {children ? <div className="mt-4">{children}</div> : null}
    </article>
  );
}

const evidenceIcons = {
  Skills: ClipboardCheck,
  Experience: Users,
  'Role fit': Target,
};

const heroCode = [
  [['k', 'def '], ['f', 'score'], ['', '(profile):']],
  [['', '  fit = '], ['f', 'skills'], ['', '(p)']],
  [['', '  depth = '], ['f', 'review'], ['', '(p)']],
  [['k', '  if '], ['', 'depth:']],
  [['k', '    return '], ['', 'ready']],
];

function useHeroMotion(ref) {
  const [phase, setPhase] = useState('still');
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const played = { current: false };
    const sync = (visible) => {
      if (media.matches) {
        played.current = false;
        setPhase('still');
        return;
      }
      if (visible) {
        played.current = true;
        setPhase('play');
        return;
      }
      setPhase(played.current ? 'pause' : 'still');
    };
    const inView = () => {
      const rect = node.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.92 && rect.bottom > window.innerHeight * 0.08;
    };
    sync(inView());
    const observer = new IntersectionObserver(([entry]) => {
      sync(entry.isIntersecting && entry.intersectionRatio >= 0.28);
    }, { threshold: [0, 0.28, 0.6] });
    observer.observe(node);
    const onChange = () => sync(inView());
    media.addEventListener('change', onChange);
    return () => {
      observer.disconnect();
      media.removeEventListener('change', onChange);
    };
  }, [ref]);
  return phase;
}

function EvalBoard({ study }) {
  const ref = useRef(null);
  const phase = useHeroMotion(ref);
  const live = phase === 'play' || phase === 'pause';
  const evidence = study.heroEvidenceCard || { title: 'Candidate evidence', profileLabel: 'Technical profile', points: [] };
  const followUp = study.heroFollowUpCard || { title: 'Coding + AI follow-up', status: 'Code submitted', question: 'Explain your approach.' };
  const shortlist = study.heroShortlistCard || { title: 'Stronger shortlist', rows: [] };
  return (
    <figure ref={ref} className={`mn-proof h-auto min-w-0 self-start rounded-[24px] border border-border bg-white p-7 shadow-sm sm:p-8 xl:p-4 ${live ? 'is-live' : ''} ${phase === 'pause' ? 'is-paused' : ''}`}>
      <p className="mt-1 text-base font-semibold leading-tight text-foreground">{study.heroVisualTitle}</p>
      <div className="mt-6 flex flex-col md:flex-row md:items-stretch xl:mt-3">
        <article className="mn-ev min-w-0 flex-1 rounded-2xl border border-border bg-[hsl(158_45%_98.5%)] p-4 xl:p-2.5">
          <p className="text-xs font-semibold leading-tight text-foreground">{evidence.title}</p>
          <div className="mn-profile mt-3 rounded-xl border border-border bg-white px-3 py-4 xl:mt-1.5 xl:px-2 xl:py-2">
            <div className="flex flex-col items-center text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-primary/15 bg-[hsl(158_45%_97%)] text-primary xl:h-10 xl:w-10">
                <User className="h-6 w-6 xl:h-5 xl:w-5" aria-hidden="true" />
              </span>
              <p className="mt-2 text-[11px] font-semibold leading-tight text-foreground xl:mt-1.5">{evidence.profileLabel}</p>
              <span className="mt-2.5 block h-1.5 w-4/5 rounded-full bg-primary/15 xl:mt-2" aria-hidden="true" />
              <span className="mt-1.5 block h-1.5 w-3/5 rounded-full bg-primary/10" aria-hidden="true" />
            </div>
          </div>
          <ul className="mt-3 grid grid-cols-3 gap-2 xl:mt-1.5">
            {(evidence.points || []).map((point) => {
              const Icon = evidenceIcons[point] || ClipboardCheck;
              return (
                <li key={point} className="mn-point flex flex-col items-center gap-1.5 rounded-xl border border-transparent px-1 py-1.5 text-center xl:gap-1 xl:py-1">
                  <span className="mn-point-icon flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary shadow-sm xl:h-8 xl:w-8">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="text-[10px] font-semibold leading-tight text-foreground">{point}</span>
                  <span className="mn-meter" aria-hidden="true"><span className="mn-meter-fill" /></span>
                </li>
              );
            })}
          </ul>
        </article>
        <span className="mn-join mn-join-a relative mx-auto my-1 h-5 w-px shrink-0 md:mx-3 md:my-0 md:h-px md:w-7 md:self-center" aria-hidden="true">
          <span className="mn-join-line absolute inset-0 bg-primary/40" />
          <span className="mn-join-dot" />
          <span className="mn-join-chev absolute -right-1 top-1/2 hidden h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-r border-t border-primary/50 md:block" />
        </span>
        <article className="mn-follow min-w-0 flex-1 rounded-2xl border border-primary/40 bg-[hsl(152_40%_96%)] p-4 md:flex-[1.12] xl:p-2.5">
          <p className="text-xs font-semibold leading-tight text-foreground">{followUp.title}</p>
          <div className="mn-codeblock relative mt-3 overflow-hidden rounded-xl border border-primary/15 bg-white shadow-sm xl:mt-1.5">
            <div className="flex items-center gap-1 border-b border-border bg-[hsl(158_45%_98%)] px-2.5 py-2 xl:py-1.5" aria-hidden="true">
              <span className="h-1.5 w-1.5 rounded-full bg-primary/25" />
              <span className="h-1.5 w-1.5 rounded-full bg-primary/20" />
              <span className="h-1.5 w-1.5 rounded-full bg-primary/15" />
            </div>
            <div className="mn-code-body relative grid h-[96px] grid-rows-5 items-center overflow-hidden px-2.5 py-3 xl:h-[88px] xl:py-2" aria-hidden="true">
              {heroCode.map((tokens, index) => (
                <span key={index} className={`mn-code-line mn-code-line-${index + 1}`}>
                  {tokens.map(([kind, text]) => (
                    <span key={text} className={kind === 'k' ? 'mn-tok-k' : kind === 'f' ? 'mn-tok-f' : undefined}>{text}</span>
                  ))}
                </span>
              ))}
              <span className="mn-hover-caret pointer-events-none absolute bottom-2.5 right-2.5" />
            </div>
          </div>
          <p className="mn-status mt-3 flex w-fit items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary xl:mt-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
            {followUp.status}
          </p>
          <p className="mn-ask relative mt-3 max-w-[14rem] rounded-2xl rounded-bl-md border border-primary/25 bg-white px-3 py-2.5 text-[13px] font-semibold leading-snug text-foreground shadow-sm xl:mt-1.5 xl:py-1.5">{followUp.question}</p>
        </article>
        <span className="mn-join mn-join-b relative mx-auto my-1 h-5 w-px shrink-0 md:mx-3 md:my-0 md:h-px md:w-7 md:self-center" aria-hidden="true">
          <span className="mn-join-line absolute inset-0 bg-primary/40" />
          <span className="mn-join-dot" />
          <span className="mn-join-chev absolute -right-1 top-1/2 hidden h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-r border-t border-primary/50 md:block" />
        </span>
        <article className="mn-short min-w-0 flex-1 rounded-2xl border border-border bg-[hsl(158_45%_98.5%)] p-4 xl:p-2.5">
          <p className="text-xs font-semibold leading-tight text-foreground">{shortlist.title}</p>
          <ul className="mt-3 space-y-2.5 xl:mt-1.5 xl:space-y-1.5" aria-label="Illustrative shortlist">
            {(shortlist.rows || []).map((row, index) => (
              <li key={index} className={`flex items-center gap-2.5 rounded-xl border px-2.5 py-2.5 xl:gap-2 xl:px-2 xl:py-1.5 ${row.selected ? 'mn-pick border-primary/40 bg-[hsl(152_40%_96%)]' : 'mn-rest border-border bg-white'}`}>
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border xl:h-8 xl:w-8 ${row.selected ? 'border-primary/25 bg-white text-primary' : 'border-border bg-[hsl(158_45%_98%)] text-primary/40'}`} aria-hidden="true">
                  <User className="h-4 w-4 xl:h-3.5 xl:w-3.5" />
                </span>
                <span className="min-w-0 flex-1" aria-hidden="true">
                  <span className={`block h-1.5 rounded-full ${row.selected ? 'w-4/5 bg-primary/30' : 'w-3/5 bg-primary/10'}`} />
                  <span className={`mt-1.5 block h-1.5 rounded-full ${row.selected ? 'w-1/2 bg-primary/20' : 'w-2/5 bg-primary/10'}`} />
                </span>
                <span className={`h-2 w-2 shrink-0 rounded-full ${row.selected ? 'mn-verify bg-primary' : 'bg-primary/15'}`} aria-hidden="true" />
              </li>
            ))}
          </ul>
        </article>
      </div>
      <div className="mt-5 flex items-center gap-2 border-t border-border pt-5 xl:mt-3 xl:pt-3">
        <p className="text-xs font-medium leading-snug text-muted-foreground">{study.heroProofLine}</p>
      </div>
      <style>{`
        .mn-proof .mn-code-line {
          display: block;
          overflow: hidden;
          max-width: 100%;
          height: 14px;
          color: hsl(222 12% 38%);
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 10px;
          line-height: 14px;
          white-space: pre;
          clip-path: inset(0 0 0 0);
        }
        .mn-proof .mn-code-line::after {
          content: '';
          position: absolute;
          top: 1px;
          width: 1px;
          height: 11px;
          background: hsl(165 37% 28%);
          opacity: 0;
        }
        .mn-proof .mn-code-line { position: relative; }
        .mn-tok-k { color: hsl(165 37% 30%); }
        .mn-tok-f { color: hsl(210 36% 40%); }
        .mn-meter { display: block; height: 4px; width: 24px; overflow: hidden; border-radius: 999px; background: hsl(165 37% 22% / 0.16); }
        .mn-meter-fill { display: block; height: 100%; width: 100%; transform-origin: left center; background: hsl(165 37% 22% / 0.28); }
        .mn-join-dot {
          position: absolute;
          top: 50%;
          left: 0;
          width: 5px;
          height: 5px;
          margin-top: -2.5px;
          border-radius: 999px;
          background: hsl(165 37% 28%);
          box-shadow: 0 0 0 2px hsl(152 45% 92%);
          opacity: 0;
        }
        .mn-hover-caret { width: 1px; height: 11px; background: hsl(165 37% 28%); opacity: 0; }
        .mn-ev, .mn-follow, .mn-short, .mn-point, .mn-point-icon, .mn-pick, .mn-rest, .mn-profile, .mn-codeblock, .mn-ask, .mn-status {
          transition: transform 280ms ease, background-color 280ms ease, border-color 280ms ease, box-shadow 280ms ease, opacity 280ms ease;
        }
        .mn-rest { opacity: 0.7; }
        .mn-pick { transform: translateX(4px); }
        .mn-proof.is-live { --hero-cycle: 8s; }
        .mn-proof.is-live .mn-profile { animation: mn-hero-profile var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-point:nth-child(1) .mn-point-icon { animation: mn-hero-point-1 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-point:nth-child(2) .mn-point-icon { animation: mn-hero-point-2 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-point:nth-child(3) .mn-point-icon { animation: mn-hero-point-3 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-point:nth-child(1) .mn-meter-fill { animation: mn-hero-meter-1 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-point:nth-child(2) .mn-meter-fill { animation: mn-hero-meter-2 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-point:nth-child(3) .mn-meter-fill { animation: mn-hero-meter-3 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-ev { animation: mn-hero-ready var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-join-a .mn-join-line { animation: mn-hero-line-a var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-join-b .mn-join-line { animation: mn-hero-line-b var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-join-a .mn-join-dot { animation: mn-hero-dot-a var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-join-b .mn-join-dot { animation: mn-hero-dot-b var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-join-a .mn-join-chev { animation: mn-hero-chev-a var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-join-b .mn-join-chev { animation: mn-hero-chev-b var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-1 { animation: mn-hero-type-1 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-2 { animation: mn-hero-type-2 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-3 { animation: mn-hero-type-3 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-4 { animation: mn-hero-type-4 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-5 { animation: mn-hero-type-5 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-1::after { animation: mn-hero-caret-1 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-2::after { animation: mn-hero-caret-2 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-3::after { animation: mn-hero-caret-3 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-4::after { animation: mn-hero-caret-4 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-code-line-5::after { animation: mn-hero-caret-5 var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-status { animation: mn-hero-status var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-ask { animation: mn-hero-ask var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-follow { animation: mn-hero-follow var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-pick { animation: mn-hero-pick var(--hero-cycle) linear infinite; }
        .mn-proof.is-live .mn-verify { animation: mn-hero-verify var(--hero-cycle) linear infinite; }
        .mn-proof.is-paused * { animation-play-state: paused !important; }
        @keyframes mn-hero-profile {
          0%, 2% { box-shadow: none; }
          6%, 12% { box-shadow: 0 0 0 3px hsl(152 45% 92%); }
          20%, 100% { box-shadow: none; }
        }
        @keyframes mn-hero-point-1 {
          0%, 6% { box-shadow: 0 1px 2px hsl(222 30% 14% / 0.05); }
          10%, 96% { box-shadow: 0 0 0 3px hsl(152 45% 88%); }
          100% { box-shadow: 0 1px 2px hsl(222 30% 14% / 0.05); }
        }
        @keyframes mn-hero-point-2 {
          0%, 12% { box-shadow: 0 1px 2px hsl(222 30% 14% / 0.05); }
          16%, 96% { box-shadow: 0 0 0 3px hsl(152 45% 88%); }
          100% { box-shadow: 0 1px 2px hsl(222 30% 14% / 0.05); }
        }
        @keyframes mn-hero-point-3 {
          0%, 18% { box-shadow: 0 1px 2px hsl(222 30% 14% / 0.05); }
          22%, 96% { box-shadow: 0 0 0 3px hsl(152 45% 88%); }
          100% { box-shadow: 0 1px 2px hsl(222 30% 14% / 0.05); }
        }
        @keyframes mn-hero-meter-1 {
          0%, 8% { transform: scaleX(0); }
          16%, 96% { transform: scaleX(1); background: hsl(165 37% 30%); }
          100% { transform: scaleX(0); }
        }
        @keyframes mn-hero-meter-2 {
          0%, 14% { transform: scaleX(0); }
          22%, 96% { transform: scaleX(1); background: hsl(165 37% 30%); }
          100% { transform: scaleX(0); }
        }
        @keyframes mn-hero-meter-3 {
          0%, 20% { transform: scaleX(0); }
          28%, 96% { transform: scaleX(1); background: hsl(165 37% 30%); }
          100% { transform: scaleX(0); }
        }
        @keyframes mn-hero-ready {
          0%, 24% { box-shadow: none; }
          30%, 36% { box-shadow: 0 0 0 3px hsl(152 45% 90%); }
          44%, 100% { box-shadow: none; }
        }
        @keyframes mn-hero-line-a {
          0%, 30% { opacity: 0.45; }
          36%, 42% { opacity: 1; }
          50%, 100% { opacity: 0.55; }
        }
        @keyframes mn-hero-line-b {
          0%, 70% { opacity: 0.45; }
          76%, 82% { opacity: 1; }
          90%, 100% { opacity: 0.55; }
        }
        @keyframes mn-hero-dot-a {
          0%, 30% { opacity: 0; transform: translateX(0); }
          34% { opacity: 1; transform: translateX(0); }
          42% { opacity: 1; transform: translateX(16px); }
          48%, 100% { opacity: 0; transform: translateX(16px); }
        }
        @keyframes mn-hero-dot-b {
          0%, 70% { opacity: 0; transform: translateX(0); }
          74% { opacity: 1; transform: translateX(0); }
          82% { opacity: 1; transform: translateX(16px); }
          88%, 100% { opacity: 0; transform: translateX(16px); }
        }
        @keyframes mn-hero-dot-a-y {
          0%, 30% { opacity: 0; transform: translateY(0); }
          34% { opacity: 1; transform: translateY(0); }
          42% { opacity: 1; transform: translateY(12px); }
          48%, 100% { opacity: 0; transform: translateY(12px); }
        }
        @keyframes mn-hero-dot-b-y {
          0%, 70% { opacity: 0; transform: translateY(0); }
          74% { opacity: 1; transform: translateY(0); }
          82% { opacity: 1; transform: translateY(12px); }
          88%, 100% { opacity: 0; transform: translateY(12px); }
        }
        @keyframes mn-hero-chev-a {
          0%, 30% { opacity: 0.4; }
          38%, 46% { opacity: 1; }
          54%, 100% { opacity: 0.75; }
        }
        @keyframes mn-hero-chev-b {
          0%, 70% { opacity: 0.4; }
          78%, 86% { opacity: 1; }
          94%, 100% { opacity: 0.75; }
        }
        @keyframes mn-hero-type-1 {
          0%, 34% { clip-path: inset(0 100% 0 0); }
          35.2% { clip-path: inset(0 80% 0 0); }
          36.4% { clip-path: inset(0 60% 0 0); }
          37.6% { clip-path: inset(0 40% 0 0); }
          38.8% { clip-path: inset(0 18% 0 0); }
          40%, 96% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(0 100% 0 0); }
        }
        @keyframes mn-hero-type-2 {
          0%, 40% { clip-path: inset(0 100% 0 0); }
          41.2% { clip-path: inset(0 80% 0 0); }
          42.4% { clip-path: inset(0 60% 0 0); }
          43.6% { clip-path: inset(0 40% 0 0); }
          44.8% { clip-path: inset(0 18% 0 0); }
          46%, 96% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(0 100% 0 0); }
        }
        @keyframes mn-hero-type-3 {
          0%, 46% { clip-path: inset(0 100% 0 0); }
          47.2% { clip-path: inset(0 80% 0 0); }
          48.4% { clip-path: inset(0 60% 0 0); }
          49.6% { clip-path: inset(0 40% 0 0); }
          50.8% { clip-path: inset(0 18% 0 0); }
          52%, 96% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(0 100% 0 0); }
        }
        @keyframes mn-hero-type-4 {
          0%, 52% { clip-path: inset(0 100% 0 0); }
          53.2% { clip-path: inset(0 78% 0 0); }
          54.4% { clip-path: inset(0 52% 0 0); }
          55.6% { clip-path: inset(0 26% 0 0); }
          57%, 96% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(0 100% 0 0); }
        }
        @keyframes mn-hero-type-5 {
          0%, 57% { clip-path: inset(0 100% 0 0); }
          58.2% { clip-path: inset(0 80% 0 0); }
          59.4% { clip-path: inset(0 58% 0 0); }
          60.6% { clip-path: inset(0 34% 0 0); }
          61.8% { clip-path: inset(0 14% 0 0); }
          63%, 96% { clip-path: inset(0 0 0 0); }
          100% { clip-path: inset(0 100% 0 0); }
        }
        @keyframes mn-hero-caret-1 {
          0%, 34% { opacity: 0; left: 0; }
          35.2% { opacity: 1; left: 20%; }
          36.4% { opacity: 0; left: 40%; }
          37.6% { opacity: 1; left: 60%; }
          38.8% { opacity: 0; left: 82%; }
          40%, 100% { opacity: 0; left: 100%; }
        }
        @keyframes mn-hero-caret-2 {
          0%, 40% { opacity: 0; left: 0; }
          41.2% { opacity: 1; left: 20%; }
          42.4% { opacity: 0; left: 40%; }
          43.6% { opacity: 1; left: 60%; }
          44.8% { opacity: 0; left: 82%; }
          46%, 100% { opacity: 0; left: 100%; }
        }
        @keyframes mn-hero-caret-3 {
          0%, 46% { opacity: 0; left: 0; }
          47.2% { opacity: 1; left: 20%; }
          48.4% { opacity: 0; left: 40%; }
          49.6% { opacity: 1; left: 60%; }
          50.8% { opacity: 0; left: 82%; }
          52%, 100% { opacity: 0; left: 100%; }
        }
        @keyframes mn-hero-caret-4 {
          0%, 52% { opacity: 0; left: 0; }
          53.2% { opacity: 1; left: 22%; }
          54.4% { opacity: 0; left: 48%; }
          55.6% { opacity: 1; left: 74%; }
          57%, 100% { opacity: 0; left: 100%; }
        }
        @keyframes mn-hero-caret-5 {
          0%, 57% { opacity: 0; left: 0; }
          58.2% { opacity: 1; left: 20%; }
          59.4% { opacity: 0; left: 42%; }
          60.6% { opacity: 1; left: 66%; }
          61.8% { opacity: 0; left: 86%; }
          63%, 100% { opacity: 0; left: 100%; }
        }
        @keyframes mn-hero-status {
          0%, 63% { background-color: transparent; box-shadow: none; }
          68%, 74% { background-color: hsl(152 45% 92%); box-shadow: 0 0 0 3px hsl(152 45% 94%); }
          80%, 100% { background-color: transparent; box-shadow: none; }
        }
        @keyframes mn-hero-ask {
          0%, 66% { opacity: 0; transform: translateY(4px); }
          74%, 96% { opacity: 1; transform: none; }
          100% { opacity: 0; transform: translateY(4px); }
        }
        @keyframes mn-hero-follow {
          0%, 68% { box-shadow: none; }
          74%, 82% { box-shadow: 0 0 0 3px hsl(152 45% 90%); }
          90%, 100% { box-shadow: none; }
        }
        @keyframes mn-hero-pick {
          0%, 78% { box-shadow: none; background-color: hsl(152 40% 96%); }
          84%, 90% { box-shadow: 0 0 0 3px hsl(152 42% 86%); background-color: hsl(152 42% 94%); }
          96%, 100% { box-shadow: none; background-color: hsl(152 40% 96%); }
        }
        @keyframes mn-hero-verify {
          0%, 80% { opacity: 0; transform: scale(0.7); }
          86%, 96% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.7); }
        }
        @keyframes mn-hover-blink {
          0%, 45% { opacity: 0.85; }
          50%, 100% { opacity: 0; }
        }
        @media (hover: hover) and (pointer: fine) {
          .mn-ev:hover { transform: translateY(-2px); }
          .mn-ev:hover .mn-profile { border-color: hsl(165 37% 22% / 0.35); box-shadow: 0 0 0 3px hsl(152 45% 92%); }
          .mn-ev:hover .mn-point { background: hsl(152 45% 95%); }
          .mn-ev:hover .mn-point-icon { background: hsl(152 42% 90%); }
          .mn-follow:hover { transform: translateY(-2px); border-color: hsl(165 37% 22% / 0.55); }
          .mn-follow:hover .mn-codeblock { border-color: hsl(165 37% 22% / 0.45); box-shadow: 0 0 0 3px hsl(152 45% 94%); }
          .mn-follow:hover .mn-hover-caret { animation: mn-hover-blink 1.05s steps(1) infinite; }
          .mn-follow:hover .mn-ask { box-shadow: 0 0 0 3px hsl(152 45% 92%); }
          .mn-short:hover .mn-pick { transform: translateX(8px); border-color: hsl(165 37% 22% / 0.62); }
          .mn-short:hover .mn-rest { opacity: 0.42; }
        }
        @media (max-width: 767px) {
          .mn-join-dot { top: 0; left: 50%; margin-top: 0; margin-left: -2.5px; }
          .mn-proof.is-live .mn-join-a .mn-join-dot { animation-name: mn-hero-dot-a-y; }
          .mn-proof.is-live .mn-join-b .mn-join-dot { animation-name: mn-hero-dot-b-y; }
          .mn-pick { transform: none; }
          .mn-short:hover .mn-pick { transform: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .mn-proof.is-live,
          .mn-proof.is-live *,
          .mn-ev:hover,
          .mn-follow:hover,
          .mn-short:hover .mn-pick,
          .mn-short:hover .mn-rest,
          .mn-follow:hover .mn-hover-caret {
            animation: none !important;
            transition: none;
          }
          .mn-proof .mn-code-line { clip-path: none; }
          .mn-proof .mn-code-line::after, .mn-join-dot, .mn-hover-caret { opacity: 0; }
          .mn-ask, .mn-meter-fill, .mn-verify { opacity: 1; transform: none; }
          .mn-rest { opacity: 0.7; }
          .mn-pick { transform: none; }
        }
      `}</style>
    </figure>
  );
}

const approachCards = [
  {
    title: 'Applicant scoring',
    body: 'Role-fit evidence from resume, skills and experience.',
  },
  {
    title: 'AI interview',
    body: 'Communication, reasoning and attitude signals.',
  },
  {
    title: 'Coding assessment',
    body: 'Technical ability and problem-solving validated.',
  },
  {
    title: 'AI follow-up',
    body: 'Deeper validation on gaps, clarifications and fit.',
  },
];

const approachSignals = ['Skills verified', 'Role fit', 'Communication', 'Technical ability'];

export default function ManestStudy({ study }) {
  const flowRef = useRef(null);
  const flowPhase = useFlowMotion(flowRef);
  const [productRef, productSeen] = useSeen(0.25);
  const [resultRef, resultSeen] = useSeen(0.25);
  const [whyRef, whySeen] = useSeen(0.25);
  const { lead, rest } = splitQuote(study.quote);
  return (
    <>
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1440px] items-start gap-6 px-4 pb-8 pt-6 sm:px-6 lg:gap-8 lg:px-8 lg:pb-8 lg:pt-8 xl:grid-cols-[minmax(0,32rem)_minmax(0,1fr)] xl:items-start">
          <div className="min-w-0 self-start">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{study.category}</p>
            <div className="mt-3 flex items-center gap-3 sm:gap-4">
              <div className="flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-white px-2 py-1 shadow-sm">
                <img src={study.assets.logo} alt="" className="h-8 w-auto max-w-none scale-[1.3] object-contain" />
              </div>
              <p className="text-2xl font-bold leading-tight tracking-[-0.03em] text-foreground sm:text-3xl">{study.customer}</p>
            </div>
            <h1 className="mt-6 max-w-xl text-2xl font-bold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-3xl">{study.headline}</h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">{study.context}</p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {(study.heroProofChips || []).map((item) => (
                <li key={item} className="rounded-full border border-primary/20 bg-[hsl(158_45%_98%)] px-3 py-1 text-xs font-semibold text-primary">{item}</li>
              ))}
            </ul>
          </div>
          <div className="min-w-0 xl:self-center">
            <EvalBoard study={study} />
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Customer at a glance</p>
          <div className="mt-5 grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 lg:grid-cols-3">
            {study.glance.map(([label, value]) => {
              const proof = label === 'Proof' ? value.split(' + ') : null;
              const main = proof ? proof[0] : value;
              const secondary = proof && proof.length > 1 ? `+ ${proof.slice(1).join(' + ')}` : '';
              return (
                <article key={label} className="mn-glance flex h-full items-center gap-5 rounded-2xl border border-border bg-white px-6 py-4 shadow-sm sm:gap-6 sm:px-7">
                  {label === 'Customer' ? (
                    <span className="mn-glance-mark flex h-14 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-primary/10 bg-[hsl(158_45%_97%)] px-2 shadow-sm">
                      <img src={study.assets.logo} alt="" className="h-20 w-auto max-w-none scale-[1.45] object-contain" />
                    </span>
                  ) : (
                    <span className="mn-glance-mark flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/10 bg-[hsl(158_45%_97%)] text-primary">
                      {label === 'Focus' ? <Code2 className="h-5 w-5" aria-hidden="true" /> : <Users className="h-5 w-5" aria-hidden="true" />}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">{label}</p>
                    <p className="mt-0.5 text-lg font-semibold leading-tight text-foreground">{main}</p>
                    {secondary ? <p className="mt-0.5 text-xs font-medium leading-snug text-muted-foreground">{secondary}</p> : null}
                  </div>
                </article>
              );
            })}
          </div>
          <style>{`
            .mn-glance, .mn-glance-mark { transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease, background-color 220ms ease, color 220ms ease; }
            @media (hover: hover) and (pointer: fine) {
              .mn-glance:hover {
                transform: translateY(-2px);
                border-color: hsl(165 37% 22% / 0.35);
                box-shadow: 0 8px 18px hsl(164 33% 10% / 0.06);
              }
              .mn-glance:hover .mn-glance-mark {
                background-color: hsl(152 40% 94%);
                border-color: hsl(165 37% 22% / 0.22);
              }
            }
            @media (prefers-reduced-motion: reduce) {
              .mn-glance, .mn-glance:hover, .mn-glance-mark {
                transform: none;
                transition: none;
              }
            }
          `}</style>
        </div>
      </section>

      <CaseStudyChallenge study={study} />

      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">The SearchJob.ai approach</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">{study.approach.title}</h2>
          <p className="mt-3 w-full max-w-none text-base leading-relaxed text-[hsl(215_14%_40%)]">{study.approach.body}</p>
          <section className="mt-8 overflow-hidden rounded-2xl border border-primary/20 bg-[hsl(158_45%_98%)] shadow-sm" aria-labelledby="manest-evidence-title">
            <header className="border-b border-primary/15 bg-white/55 px-5 py-5 sm:px-6">
              <h3 id="manest-evidence-title" className="text-base font-semibold text-foreground">Evidence before live interview</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">A complete candidate profile built from multiple data points.</p>
            </header>
            <ul className="grid sm:grid-cols-2">
              {approachCards.map((card, index) => (
                <li
                  key={card.title}
                  className={`flex gap-3 px-5 py-5 sm:px-6 ${index % 2 === 0 ? 'sm:border-r sm:border-primary/15' : ''} ${index === 1 ? 'border-t border-primary/15 sm:border-t-0' : index > 1 ? 'border-t border-primary/15' : ''}`}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-foreground">{card.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <footer className="flex flex-col gap-3 border-t border-primary/15 bg-white/55 px-5 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
              <ul className="flex flex-wrap gap-x-5 gap-y-2" aria-label="Candidate evidence signals">
                {approachSignals.map((signal) => (
                  <li key={signal} className="flex items-center gap-2 text-sm text-foreground">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    {signal}
                  </li>
                ))}
              </ul>
              <p className="shrink-0 text-sm font-semibold text-primary">Live interview</p>
            </footer>
          </section>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">How the workflow changed</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
            From submitted code to explained thinking.
          </h2>
          <p className="mt-3 w-full max-w-none text-base leading-relaxed text-muted-foreground">
            The most differentiated part of the story is the coding follow-up: the system did not stop at whether code was submitted; it asked candidates to explain their own solution.
          </p>

          <ol
            className="mn-coding-flow relative mt-8 grid overflow-hidden rounded-2xl border border-primary/20 bg-[hsl(158_45%_98%)] sm:grid-cols-2 lg:grid-cols-4"
            aria-label="Coding follow-up workflow"
          >
            {codingFollowUpStages.map((stage, index) => {
              const Icon = stage.icon;
              const separator =
                index === 0
                  ? ''
                  : index === 1
                    ? 'border-t border-primary/15 sm:border-l sm:border-t-0'
                    : index === 2
                      ? 'border-t border-primary/15 lg:border-l lg:border-t-0'
                      : 'border-t border-primary/15 sm:border-l sm:border-t-0';
              return (
                <li
                  key={stage.label}
                  className={`relative flex min-h-44 flex-col px-5 py-5 sm:px-6 ${separator}`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${stage.emphasis ? 'bg-primary text-primary-foreground' : 'bg-white text-primary shadow-sm'}`}>
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-1.5 text-base font-semibold leading-snug text-foreground">{stage.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{stage.detail}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {false && <section className="border-b border-border bg-white">
        <div ref={flowRef} className={`mn-flow mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16 ${flowPhase === 'play' || flowPhase === 'pause' ? 'is-live' : ''} ${flowPhase === 'pause' ? 'is-paused' : ''}`}>
          <h2 className="max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">{study.workflowTitle}</h2>
          <div className="mn-track relative z-10 mt-8">
            {flow.map((step, index) => {
              const Icon = step.icon;
              const active = step.label === 'AI follow-up';
              const tint = flowTints[step.label];
              const isCodingTest = step.label === 'Coding test';
              return (
                <React.Fragment key={step.label}>
                  <div className={`mn-stage mn-stage-${index} relative flex min-w-0 flex-col items-center rounded-2xl border px-2 py-3 text-center ${isCodingTest ? 'mn-stage-code gap-1.5' : 'justify-center gap-2'} ${active ? 'border-primary/40 bg-[hsl(152_42%_96%)]' : 'border-border bg-white'}`}>
                    <span className={`mn-stage-icon flex h-8 w-8 items-center justify-center rounded-lg ${active ? 'bg-white text-primary' : tint}`}>
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="mn-stage-label text-xs font-semibold leading-tight text-[hsl(222_30%_14%)]">{step.label}</span>
                    {isCodingTest ? (
                      <>
                        <span className="w-full border-t border-border" aria-hidden="true" />
                        <ul className="mn-code-points grid w-full grid-cols-2 gap-x-2 gap-y-1 text-left" aria-label="Coding test evaluation steps">
                          {codingTestPoints.map((point) => (
                            <li key={point} className="flex min-w-0 gap-1 text-[11px] font-medium leading-[1.2] text-muted-foreground">
                              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/55" aria-hidden="true" />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </div>
                  {index < flow.length - 1 ? <span className={`mn-arrow mn-arrow-${index}`} aria-hidden="true"><span>→</span></span> : null}
                </React.Fragment>
              );
            })}
          </div>
          <style>{`
            .mn-flow { --flow-cycle: 9.6s; }
            .mn-flow .mn-tone-mint { background: hsl(152 42% 97.5%); border-color: hsl(158 28% 86%); }
            .mn-flow .mn-tone-blue { background: hsl(210 55% 97.5%); border-color: hsl(210 32% 88%); }
            .mn-flow .mn-tone-peach { background: hsl(28 75% 97.5%); border-color: hsl(28 42% 88%); }
            .mn-flow .mn-tone-lavender { background: hsl(262 42% 97.5%); border-color: hsl(262 28% 89%); }
            .mn-track, .mn-stories { display: flex; flex-direction: column; align-items: center; row-gap: 0.35rem; }
            .mn-stage, .mn-story { width: min(100%, 24rem); min-width: 0; }
            .mn-stage { min-height: 9.75rem; }
            .mn-code-points { list-style: none; margin: 0; padding: 0; }
            .mn-flow .mn-illu { max-width: 100%; min-width: 0; }
            .mn-stage, .mn-stage-icon, .mn-arrow, .mn-story, .mn-step-arrow, .mn-drop-line, .mn-illu .shadow-sm, .mn-laptop, .mn-ask-icon, .mn-bulb, .mn-wave {
              transition: transform 280ms ease, border-color 280ms ease, box-shadow 280ms ease, background-color 280ms ease, color 300ms ease;
            }
            .mn-arrow, .mn-step-arrow {
              position: relative;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 1.15rem;
              color: hsl(165 37% 22% / 0.4);
              font-size: 0.95rem;
              line-height: 1;
              transform: rotate(90deg);
            }
            .mn-arrow::after, .mn-step-arrow::after {
              content: '';
              position: absolute;
              left: 0;
              top: 50%;
              width: 5px;
              height: 5px;
              margin-top: -2.5px;
              border-radius: 999px;
              background: hsl(168 42% 30%);
              box-shadow: 0 0 0 3px hsl(168 45% 42% / 0.18);
              opacity: 0;
              pointer-events: none;
            }
            .mn-drop-line { display: none; }
            .mn-flow .mn-code-line { display: block; transform-origin: left center; }
            .mn-wave-bar { transform-origin: bottom center; }
            @media (min-width: 1100px) {
              .mn-track {
                display: grid;
                grid-template-columns: minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr);
                column-gap: 0.4rem;
                row-gap: 0;
                align-items: center;
              }
              .mn-stories {
                display: grid;
                grid-template-columns: minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1fr);
                column-gap: 0.65rem;
                align-items: stretch;
              }
              .mn-stage { width: auto; height: 9.75rem; min-height: 9.75rem; }
              .mn-stage-label { white-space: nowrap; }
              .mn-arrow, .mn-step-arrow { transform: none; }
              .mn-drop-line {
                display: block;
                width: 1.25rem;
                height: 2.5rem;
                margin-left: -0.625rem;
                z-index: 2;
              }
            }
            .mn-drop-line::before {
              content: '';
              position: absolute;
              left: 50%;
              top: 0;
              bottom: 0;
              width: 1px;
              margin-left: -0.5px;
              background: hsl(165 37% 22% / 0.35);
              transform: scaleY(1);
              transform-origin: top center;
            }
            .mn-drop-dot {
              position: absolute;
              left: 50%;
              top: 0;
              width: 6px;
              height: 6px;
              margin-left: -3px;
              border-radius: 999px;
              background: hsl(168 42% 30%);
              box-shadow: 0 0 0 3px hsl(168 45% 90%);
              opacity: 0;
            }
            .mn-drop-chev {
              position: absolute;
              left: 50%;
              bottom: 0;
              width: 6px;
              height: 6px;
              margin-left: -4px;
              border-right: 1.5px solid hsl(165 37% 22% / 0.45);
              border-bottom: 1.5px solid hsl(165 37% 22% / 0.45);
              transform: rotate(45deg);
            }
            .mn-flow.is-live .mn-stage-0 { animation: mn-flow-stage-0 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-stage-1 { animation: mn-flow-stage-1 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-stage-code { animation: mn-flow-code-card var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-stage-3 { animation: mn-flow-stage-3 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-stage-4 { animation: mn-flow-stage-4 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-stage-5 { animation: mn-flow-stage-5 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-arrow-0::after { animation: mn-flow-shine-0 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-arrow-1::after { animation: mn-flow-shine-1 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-arrow-2::after { animation: mn-flow-shine-2 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-arrow-3::after { animation: mn-flow-shine-3 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-arrow-4::after { animation: mn-flow-shine-4 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-step-arrow-0::after { animation: mn-flow-step-0 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-step-arrow-1::after { animation: mn-flow-step-1 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-step-arrow-2::after { animation: mn-flow-step-2 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-drop-line::before { animation: mn-flow-draw var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-drop-dot { animation: mn-flow-dot var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-drop-chev { animation: mn-flow-chev var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-after { animation: mn-flow-after var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-laptop .mn-code-line:nth-child(1) { animation: mn-flow-type-1 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-laptop .mn-code-line:nth-child(2) { animation: mn-flow-type-2 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-laptop .mn-code-line:nth-child(3) { animation: mn-flow-type-3 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-laptop .mn-code-line:nth-child(4) { animation: mn-flow-type-4 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-code-check { animation: mn-flow-check var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-ask-icon { animation: mn-flow-ask var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-why-bubble { animation: mn-flow-bubble var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-wave-bar:nth-child(1) { animation: mn-flow-wave-1 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-wave-bar:nth-child(2) { animation: mn-flow-wave-2 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-wave-bar:nth-child(3) { animation: mn-flow-wave-3 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-wave-bar:nth-child(4) { animation: mn-flow-wave-4 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-wave-bar:nth-child(5) { animation: mn-flow-wave-5 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-bulb { animation: mn-flow-bulb var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-think:nth-child(1) { animation: mn-flow-think-1 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-think:nth-child(2) { animation: mn-flow-think-2 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-think:nth-child(3) { animation: mn-flow-think-3 var(--flow-cycle) linear infinite; }
            .mn-flow.is-live .mn-story-3 { animation: mn-flow-outcome var(--flow-cycle) linear infinite; }
            .mn-flow.is-paused * { animation-play-state: paused !important; }
            @keyframes mn-flow-stage-0 { 0%, 2% { box-shadow: none; } 5% { box-shadow: 0 0 0 3px hsl(168 40% 90%); } 9%, 100% { box-shadow: none; } }
            @keyframes mn-flow-stage-1 { 0%, 8% { box-shadow: none; } 12% { box-shadow: 0 0 0 3px hsl(168 40% 90%); } 16%, 100% { box-shadow: none; } }
            @keyframes mn-flow-code-card {
              0%, 15%, 32%, 100% { background-color: white; box-shadow: none; }
              19%, 26% { background-color: hsl(28 80% 97%); box-shadow: 0 0 0 3px hsl(24 70% 58% / 0.28); }
            }
            @keyframes mn-flow-stage-3 { 0%, 86% { box-shadow: none; } 90% { box-shadow: 0 0 0 3px hsl(168 40% 90%); } 95%, 100% { box-shadow: none; } }
            @keyframes mn-flow-stage-4 { 0%, 91% { box-shadow: none; } 94% { box-shadow: 0 0 0 3px hsl(168 40% 90%); } 98%, 100% { box-shadow: none; } }
            @keyframes mn-flow-stage-5 { 0%, 95% { box-shadow: none; } 98% { box-shadow: 0 0 0 3px hsl(152 45% 88%); } }
            @keyframes mn-flow-shine-0 { 0%, 5% { opacity: 0; transform: translateX(0); } 7% { opacity: 1; transform: translateX(0); } 12% { opacity: 0; transform: translateX(8px); } 100% { opacity: 0; } }
            @keyframes mn-flow-shine-1 { 0%, 12% { opacity: 0; transform: translateX(0); } 14% { opacity: 1; transform: translateX(0); } 19% { opacity: 0; transform: translateX(8px); } 100% { opacity: 0; } }
            @keyframes mn-flow-shine-2 { 0%, 86% { opacity: 0; transform: translateX(0); } 88% { opacity: 1; transform: translateX(0); } 93% { opacity: 0; transform: translateX(8px); } 100% { opacity: 0; } }
            @keyframes mn-flow-shine-3 { 0%, 91% { opacity: 0; transform: translateX(0); } 93% { opacity: 1; transform: translateX(0); } 97% { opacity: 0; transform: translateX(8px); } 100% { opacity: 0; } }
            @keyframes mn-flow-shine-4 { 0%, 95% { opacity: 0; transform: translateX(0); } 97% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(8px); } }
            @keyframes mn-flow-step-0 { 0%, 46% { opacity: 0; transform: translateX(0); } 48% { opacity: 1; transform: translateX(0); } 53% { opacity: 0; transform: translateX(8px); } 100% { opacity: 0; } }
            @keyframes mn-flow-step-1 { 0%, 56% { opacity: 0; transform: translateX(0); } 58% { opacity: 1; transform: translateX(0); } 63% { opacity: 0; transform: translateX(8px); } 100% { opacity: 0; } }
            @keyframes mn-flow-step-2 { 0%, 68% { opacity: 0; transform: translateX(0); } 70% { opacity: 1; transform: translateX(0); } 75% { opacity: 0; transform: translateX(8px); } 100% { opacity: 0; } }
            @keyframes mn-flow-draw { 0%, 20% { transform: scaleY(0); } 30%, 100% { transform: scaleY(1); } }
            @keyframes mn-flow-dot {
              0%, 22% { opacity: 0; transform: translateY(0); }
              26% { opacity: 1; transform: translateY(0); }
              34% { opacity: 1; transform: translateY(2rem); }
              42%, 100% { opacity: 0; transform: translateY(2rem); }
            }
            @keyframes mn-flow-chev {
              0%, 24% { opacity: 0.4; transform: rotate(45deg); }
              32%, 40% { opacity: 1; transform: translateY(5px) rotate(45deg); }
              48%, 100% { opacity: 0.75; transform: rotate(45deg); }
            }
            @keyframes mn-flow-after {
              0%, 32%, 48%, 100% { background-color: transparent; }
              36%, 42% { background-color: hsl(152 45% 90%); }
            }
            @keyframes mn-flow-type-1 { 0%, 29% { transform: scaleX(0); } 35%, 100% { transform: scaleX(1); } }
            @keyframes mn-flow-type-2 { 0%, 33% { transform: scaleX(0); } 39%, 100% { transform: scaleX(1); } }
            @keyframes mn-flow-type-3 { 0%, 37% { transform: scaleX(0); } 43%, 100% { transform: scaleX(1); } }
            @keyframes mn-flow-type-4 { 0%, 41% { transform: scaleX(0); } 47%, 100% { transform: scaleX(1); } }
            @keyframes mn-flow-check { 0%, 45% { opacity: 0; transform: scale(0.82); } 51%, 100% { opacity: 1; transform: none; } }
            @keyframes mn-flow-ask { 0%, 49%, 60%, 100% { transform: none; } 53% { transform: scale(1.06); background-color: hsl(210 60% 96%); } }
            @keyframes mn-flow-bubble { 0%, 50% { opacity: 0; transform: translateY(4px); } 57%, 100% { opacity: 1; transform: none; } }
            @keyframes mn-flow-wave-1 { 0%, 59% { transform: scaleY(0.4); } 63% { transform: scaleY(1); } 67% { transform: scaleY(0.5); } 72% { transform: scaleY(0.85); } 76%, 100% { transform: scaleY(1); } }
            @keyframes mn-flow-wave-2 { 0%, 60% { transform: scaleY(0.7); } 64% { transform: scaleY(0.4); } 68% { transform: scaleY(1); } 73% { transform: scaleY(0.55); } 77%, 100% { transform: scaleY(1); } }
            @keyframes mn-flow-wave-3 { 0%, 61% { transform: scaleY(0.5); } 65% { transform: scaleY(0.9); } 69% { transform: scaleY(0.45); } 74% { transform: scaleY(1); } 78%, 100% { transform: scaleY(1); } }
            @keyframes mn-flow-wave-4 { 0%, 62% { transform: scaleY(0.85); } 66% { transform: scaleY(0.4); } 70% { transform: scaleY(1); } 75% { transform: scaleY(0.6); } 79%, 100% { transform: scaleY(1); } }
            @keyframes mn-flow-wave-5 { 0%, 63% { transform: scaleY(0.45); } 67% { transform: scaleY(0.8); } 71% { transform: scaleY(0.4); } 76% { transform: scaleY(0.95); } 80%, 100% { transform: scaleY(1); } }
            @keyframes mn-flow-bulb {
              0%, 72%, 90%, 100% { background-color: white; box-shadow: 0 1px 2px hsl(222 30% 14% / 0.06); }
              76%, 84% { background-color: hsl(48 92% 94%); box-shadow: 0 0 0 4px hsl(48 80% 86% / 0.9); }
            }
            @keyframes mn-flow-think-1 { 0%, 75% { opacity: 0.22; } 80%, 100% { opacity: 1; } }
            @keyframes mn-flow-think-2 { 0%, 79% { opacity: 0.22; } 84%, 100% { opacity: 1; } }
            @keyframes mn-flow-think-3 { 0%, 83% { opacity: 0.22; } 88%, 100% { opacity: 1; } }
            @keyframes mn-flow-outcome {
              0%, 86% { background-color: hsl(262 42% 97.5%); border-color: hsl(262 28% 89%); }
              90%, 100% { background-color: hsl(152 42% 95%); border-color: hsl(165 37% 22% / 0.42); }
            }
            @media (hover: hover) and (pointer: fine) {
              .mn-stage:hover, .mn-story:hover { transform: translateY(-3px); box-shadow: 0 10px 22px hsl(222 30% 14% / 0.06); }
              .mn-stage:hover { border-color: hsl(165 37% 22% / 0.4); }
              .mn-stage:hover .mn-stage-icon { background-color: hsl(152 45% 94%); }
              .mn-stage:hover + .mn-arrow, .mn-story:hover + .mn-step-arrow, .mn-arrow:hover, .mn-step-arrow:hover {
                color: hsl(168 45% 26%);
                transform: translateX(5px);
              }
              .mn-story:hover .mn-laptop, .mn-story:hover .mn-ask-icon, .mn-story:hover .mn-bulb, .mn-story:hover .mn-wave, .mn-story:hover .mn-illu .shadow-sm {
                box-shadow: 0 6px 14px hsl(222 30% 14% / 0.08);
              }
              .mn-flow .mn-tone-mint:hover { border-color: hsl(158 32% 62%); }
              .mn-flow .mn-tone-blue:hover { border-color: hsl(210 40% 70%); }
              .mn-flow .mn-tone-peach:hover { border-color: hsl(24 45% 70%); }
              .mn-flow .mn-tone-lavender:hover { border-color: hsl(262 30% 72%); }
              .mn-drop-line:hover { transform: translateY(5px); }
              .mn-drop-line:hover::before { background: hsl(168 45% 28% / 0.85); }
              .mn-drop-line:hover .mn-drop-dot { opacity: 1; }
            }
            @media (hover: hover) and (pointer: fine) and (max-width: 1099px) {
              .mn-stage:hover + .mn-arrow, .mn-story:hover + .mn-step-arrow, .mn-arrow:hover, .mn-step-arrow:hover {
                transform: translateY(5px) rotate(90deg);
              }
            }
            @media (prefers-reduced-motion: reduce) {
              .mn-flow.is-live, .mn-flow.is-live *, .mn-stage:hover, .mn-story:hover, .mn-arrow:hover, .mn-step-arrow:hover, .mn-drop-line:hover {
                animation: none !important;
                transition: none;
              }
              .mn-flow .mn-code-line, .mn-code-check, .mn-why-bubble, .mn-think, .mn-wave-bar { opacity: 1; transform: none; }
              .mn-drop-line::before { transform: scaleY(1); }
              .mn-drop-dot { opacity: 0; }
              .mn-arrow, .mn-step-arrow { transform: none; }
            }
            @media (prefers-reduced-motion: reduce) and (max-width: 1099px) {
              .mn-arrow, .mn-step-arrow { transform: rotate(90deg); }
            }
          `}</style>
        </div>
      </section>}

      <section className="border-b border-border">
        <div ref={productRef} className={`mn-product mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 ${productSeen ? 'is-seen' : ''}`}>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Business impact</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">The Business Impact</h2>
          <p className="mt-3 w-full max-w-none text-base leading-relaxed text-muted-foreground">
            The customer says the result was a senior-quality shortlist in days. Scoring, first-round interviews and a coding follow-up were already in place, so the team met only candidates worth meeting.
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <Card icon={ClipboardCheck} title="Candidate scoring" body="A resume is read against evaluation categories. Only evidence indicators are shown.">
              <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                <p>Role relevance</p>
                <p>Technical depth</p>
                <p>Communication</p>
              </div>
            </Card>
            <Card icon={Sparkles} title="Coding + AI follow-up" body="The follow-up question is the center of the evaluation." accent>
              <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                <p>Code submission</p>
                <p>Explain your approach.</p>
                <p>Candidate explanation</p>
              </div>
            </Card>
            <Card icon={Target} title="Candidate evidence report" body="Illustrative signals only. No private candidate record is shown.">
              <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                {['Applicant score', 'AI interview signal', 'Coding evaluation', 'Follow-up evidence', 'Recommendation'].map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {false && (
      <section className="border-b border-border">
        <div ref={resultRef} className={`mn-results mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 ${resultSeen ? 'is-seen' : ''}`}>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Business impact</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">The Business Impact</h2>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-muted-foreground">
            The customer says the result was a senior-quality shortlist in days. Scoring, first-round interviews and a coding follow-up were already in place, so the team met only candidates worth meeting.
          </p>
          <ul className="mt-6 grid gap-3 rounded-2xl border border-primary/20 bg-[hsl(158_45%_98%)] p-4 sm:grid-cols-3 sm:p-5">
            {study.resultEvidence.map((item) => (
              <li key={item.label} className="flex min-w-0 gap-3 rounded-xl border border-primary/15 bg-white px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{item.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
          {/* Replaced by the concise Business Impact summary above.
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <Card title="Faster technical screening" body="Resume, interview, coding and follow-up happen before a qualified shortlist.">
              <div className="relative h-2" aria-hidden="true">
                <span className="mn-line absolute inset-x-0 top-1/2 h-px bg-primary/30" />
                <span className="relative flex justify-between">
                  {['Resume', 'Interview', 'Coding', 'Follow-up', 'Shortlist'].map((label, index) => (
                    <span key={label} className="mn-tick h-1.5 w-1.5 rounded-full bg-primary" style={{ '--i': index }} />
                  ))}
                </span>
              </div>
              <p className="text-[11px] font-semibold leading-relaxed text-muted-foreground">Resume → AI interview → Coding → Follow-up → Qualified shortlist</p>
            </Card>
            <Card title="Evidence before interview" body="Code alone is not enough. The candidate has to explain it.">
              <ol className="space-y-1">
                {['Code submission', 'AI follow-up', 'Explanation', 'Evidence'].map((item, index) => (
                  <li key={item} className="mn-seq text-[11px] font-semibold text-muted-foreground" style={{ '--i': index }}>{item}</li>
                ))}
              </ol>
            </Card>
            <Card title="Stronger shortlist" body="Several technical profiles are scored. Only the stronger ones move into the shortlist.">
              <ul className="space-y-1.5">
                {[
                  ['Backend', true],
                  ['Machine learning', true],
                  ['Frontend', false],
                ].map(([role, selected]) => (
                  <li key={role} className={`flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${selected ? 'mn-pick border-primary/30 bg-white text-foreground' : 'mn-rest border-border bg-white text-muted-foreground'}`}>
                    {role}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <style>{`
            .mn-results .mn-line { transform: scaleX(0); transform-origin: left center; }
            .mn-results.is-seen .mn-line { transform: scaleX(1); transition: transform 500ms ease; }
            .mn-results .mn-tick { opacity: 0.25; }
            .mn-results.is-seen .mn-tick { opacity: 1; transition: opacity 250ms ease; transition-delay: calc(var(--i) * 90ms); }
            .mn-results .mn-seq { opacity: 0.3; }
            .mn-results.is-seen .mn-seq { opacity: 1; transition: opacity 280ms ease; transition-delay: calc(var(--i) * 110ms); }
            .mn-results .mn-pick, .mn-results .mn-rest { transition: transform 400ms ease, opacity 400ms ease; }
            .mn-results .mn-rest { opacity: 0.55; }
            .mn-results.is-seen .mn-pick { transform: translateX(3px); }
            .mn-results.is-seen .mn-rest { opacity: 0.4; }
            @media (prefers-reduced-motion: reduce) {
              .mn-results .mn-line, .mn-results .mn-tick, .mn-results .mn-seq, .mn-results .mn-pick, .mn-results .mn-rest { opacity: 1; transform: none; transition: none; animation: none; }
              .mn-results .mn-rest { opacity: 0.55; }
            }
          `}</style>
          */}
        </div>
      </section>
      )}
      <section ref={whyRef} className={`mn-why border-b border-border bg-white ${whySeen ? 'is-seen' : ''}`}>
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Why it matters</p>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">{study.why.title}</h2>
          <div className="mt-8 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3">
            <article className="mn-why-card flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-[hsl(222_30%_14%)]">Technical depth</h3>
              <div className="mn-why-art mt-4 flex flex-1 items-center rounded-2xl bg-[hsl(152_42%_98%)]">
                <WhyDepth />
              </div>
              <p className="mt-5 text-base font-semibold text-[hsl(222_30%_14%)]">Know their technical depth earlier.</p>
              <p className="mt-1 text-sm text-[hsl(215_14%_40%)]">Before the live interview.</p>
            </article>
            <article className="mn-why-card mn-why-main flex h-full flex-col rounded-2xl border border-primary/30 bg-[hsl(158_45%_98.5%)] p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-[hsl(222_30%_14%)]">Evidence before interviews</h3>
              <div className="mn-why-art mt-4 flex flex-1 items-center rounded-2xl bg-white/70">
                <WhyEvidence />
              </div>
              <p className="mt-5 text-base font-semibold text-[hsl(222_30%_14%)]">Multiple signals.</p>
              <p className="mt-1 text-sm font-semibold text-primary">One stronger view.</p>
            </article>
            <article className="mn-why-card flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-[hsl(222_30%_14%)]">Founder and team time</h3>
              <div className="mn-why-art mt-4 flex flex-1 items-center rounded-2xl bg-[hsl(262_42%_98%)]">
                <WhyTime />
              </div>
              <p className="mt-5 text-base font-semibold text-[hsl(222_30%_14%)]">Meet fewer.</p>
              <p className="mt-1 text-sm font-semibold text-primary">Meet better.</p>
            </article>
          </div>
          <style>{`
            .mn-glass { transform: translate(-8px, 3px); transition: transform 900ms ease; }
            .mn-why.is-seen .mn-glass { transform: translate(3px, -2px); }
            .mn-src { opacity: 0.35; }
            .mn-why.is-seen .mn-src {
              opacity: 1;
              transition: opacity 420ms ease;
              transition-delay: calc(var(--i) * 180ms);
            }
            .mn-link { stroke-dasharray: 1; stroke-dashoffset: 1; }
            .mn-why.is-seen .mn-link { stroke-dashoffset: 0; transition: stroke-dashoffset 520ms ease; transition-delay: calc(120ms + var(--i) * 180ms); }
            .mn-drop { transition: opacity 700ms ease; }
            .mn-why.is-seen .mn-drop { opacity: 0.4; transition-delay: 360ms; }
            .mn-meet { transition: background-color 500ms ease, border-color 500ms ease, box-shadow 500ms ease; }
            .mn-why.is-seen .mn-meet { background-color: hsl(152 42% 94%); border-color: hsl(165 37% 22% / 0.28); transition-delay: 900ms; }
            .mn-why-card, .mn-why-art { transition: transform 280ms ease, border-color 280ms ease, box-shadow 280ms ease, background-color 280ms ease; }
            @media (hover: hover) and (pointer: fine) {
              .mn-why-card:hover { transform: translateY(-3px); border-color: hsl(165 37% 22% / 0.4); box-shadow: 0 10px 22px hsl(222 30% 14% / 0.06); }
              .mn-why-card:hover .mn-why-art { background-color: hsl(158 45% 96%); }
              .mn-why-card:hover .mn-glass { transform: translate(8px, -4px); transition-delay: 0ms; }
            }
            @media (prefers-reduced-motion: reduce) {
              .mn-why.is-seen .mn-glass,
              .mn-why-card:hover,
              .mn-why-card:hover .mn-glass,
              .mn-glass,
              .mn-link,
              .mn-why.is-seen .mn-link,
              .mn-drop,
              .mn-why.is-seen .mn-drop,
              .mn-meet,
              .mn-why.is-seen .mn-meet,
              .mn-src {
                opacity: 1;
                transform: none;
                transition: none;
                animation: none;
                stroke-dashoffset: 0;
              }
              .mn-why .mn-drop { opacity: 0.4; }
              .mn-why .mn-meet { background-color: hsl(152 42% 94%); border-color: hsl(165 37% 22% / 0.28); }
            }
          `}</style>
        </div>
      </section>

      <CustomerInsights study={study} />

      <section className="border-b border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-12 text-center sm:px-6 lg:px-8">
          <h2 className="max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
            Want to see what this workflow would look like for your hiring team?
          </h2>
          <a href={CTA_HREF} className={`${buttonPrimary} mt-6 w-full sm:w-auto`}>
            {CTA_LABEL}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      {false && <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Customer insights</h2>
            <span className="h-px w-14 bg-primary/35" aria-hidden="true" />
          </div>
          <figure className="mt-5 grid overflow-hidden rounded-[28px] border border-[hsl(158_28%_82%)] bg-[hsl(158_45%_98%)] shadow-sm lg:grid-cols-[minmax(0,1.7fr)_minmax(220px,0.8fr)]">
            <div className="px-6 py-7 sm:px-10">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true"><Quote className="h-4 w-4" /></span>
              <blockquote className="mt-5">
                <p className="max-w-3xl text-xl font-bold leading-snug tracking-[-0.02em] text-foreground">“{lead}”</p>
                {rest ? <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">“{rest}”</p> : null}
              </blockquote>
              <div className="mt-6 h-px bg-primary/15" />
              <figcaption className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">{study.attribution}</figcaption>
            </div>
            <div className="border-t border-[hsl(158_28%_86%)] bg-[hsl(158_42%_96%)] px-6 py-7 lg:border-l lg:border-t-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">What they valued</p>
              <ul className="mt-4 space-y-2">
                {study.pdfChips.map((item) => (
                  <li key={item} className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-sm font-medium text-foreground">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </figure>
        </div>
      </section>}

    </>
  );
}

function splitQuote(quote) {
  const text = String(quote || '').trim();
  const match = text.match(/^(.+?[.!?])\s+([\s\S]+)$/);
  if (!match) return { lead: text, rest: '' };
  return { lead: match[1], rest: match[2].trim() };
}
